import { formatPrice } from '@/utils/formatPrice'
import {
  Box,
  Text,
  useColorModeValue,
  HStack,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  useMediaQuery,
} from '@chakra-ui/react'
import { useTranslations } from 'next-intl'

interface PriceRangeBarProps {
  low: number
  median: number
  high: number
  price: number
  isSalePage?: boolean
}

export const PriceRangeBar = ({
  low,
  median,
  high,
  price,
  isSalePage = false,
}: PriceRangeBarProps) => {
  const t = useTranslations()
  const [isTouch] = useMediaQuery('(pointer: coarse)')
  const track = useColorModeValue('gray.200', 'gray.600')
  const tick = useColorModeValue('gray.400', 'gray.600')
  const label = useColorModeValue('gray.600', 'whiteAlpha.500')
  const bgTooltip = useColorModeValue('gray.900', 'gray.800')

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v))

  const getPricePercent = (
    price: number,
    low: number,
    median: number,
    high: number
  ) => {
    const clampedPrice = clamp(price, low, high)

    if (median <= low || median >= high) {
      return ((clampedPrice - low) / (high - low)) * 100
    }

    if (clampedPrice <= median) {
      return ((clampedPrice - low) / (median - low)) * 50
    }

    return 50 + ((clampedPrice - median) / (high - median)) * 50
  }
  const pricePercent = getPricePercent(price, low, median, high)

  return (
    <Box w="100%">
      <Box position="relative" h="22px">
        <Box
          position="absolute"
          left={0}
          right={0}
          top="10px"
          h="2px"
          bg={track}
          rounded="full"
        />
        {[0, 50, 100].map((pct, i) => (
          <Box
            key={i}
            position="absolute"
            left={`${pct}%`}
            top="8px"
            w="2px"
            h="6px"
            bg={tick}
            transform="translateX(-1px)"
          />
        ))}

        {/* Diamond */}
        {!isSalePage && (
          <Popover placement="bottom" trigger={isTouch ? 'click' : 'hover'}>
            <PopoverTrigger>
              <Box
                position="absolute"
                left={`${pricePercent}%`}
                top="6px"
                w="10px"
                h="10px"
                bg="blue.100"
                transform="translateX(-50%) rotate(45deg)"
                cursor="pointer"
                borderWidth={1}
                borderColor="blue.600"
              />
            </PopoverTrigger>

            <PopoverContent
              w="auto"
              bg={bgTooltip}
              color="white"
              borderRadius="4px"
              border="none"
              _focus={{ boxShadow: 'none' }}
              mt={2}
            >
              <PopoverArrow bg={bgTooltip} />
              <PopoverBody py="6px" px="10px">
                <Text fontSize="14px" fontWeight="400">
                  {t('dashboard.machine-sale.tooltips.listed-price')}
                </Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </Box>
      <HStack justify="space-between" spacing={0} align="start">
        <VStack fontSize="10px" spacing={0} align="start">
          <Text>{formatPrice(low, false)}</Text>
          <Text color={label} display={isSalePage ? 'none' : 'inline'}>
            {t('dashboard.machine-sale.low')}
          </Text>
        </VStack>
        <VStack fontSize="10px" spacing={0}>
          <Text>{formatPrice(median, false)}</Text>
          <Text color={label} display={isSalePage ? 'none' : 'inline'}>
            {t('dashboard.machine-sale.median')}
          </Text>
        </VStack>
        <VStack fontSize="10px" spacing={0} align="end">
          <Text>{formatPrice(high, false)}</Text>
          <Text color={label} display={isSalePage ? 'none' : 'inline'}>
            {t('dashboard.machine-sale.high')}
          </Text>
        </VStack>
      </HStack>
    </Box>
  )
}
